import {
  FocalEventPosterImage,
  getCoverEventPosterImage,
  type FocalEventPosterImageSource,
} from '@/features/events/shared/FocalEventPosterImage';
import { AppScreen } from '@/components/ui/scene';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ErrorState } from '@/components/ui/async-state';
import { Icon } from '@/components/ui/icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { useDeleteNotification, useMarkNotificationRead, useNotifications } from '@/features/pulse/useNotifications';
import { cn } from '@/lib/utils';
import { trackBusinessNotificationAction } from '@/lib/analytics/posthog';
import { useAuth } from '@/lib/auth/auth';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { useUserOriginCoords } from '@/lib/location/locationOrigin';
import { isAllowedNotificationRoute } from '@/lib/notifications/notificationCore';
import { type DiscoveryEventDetail as RadiousEventDetail , NotificationItem, NotificationType } from '@/lib/api/apiTypes';
import { formatRelativeTime } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { router } from 'expo-router';
import { useQueries } from '@tanstack/react-query';
import {
  BellRingIcon,
  BadgeCheckIcon,
  InboxIcon,
  ShieldCheckIcon,
  Trash2Icon,
  UserRoundMinusIcon,
  UserRoundPenIcon,
  UserRoundPlusIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { FlatList, Pressable, RefreshControl, ScrollView, View, type ListRenderItem } from 'react-native';
import Swipeable from 'react-native-gesture-handler/Swipeable';

type BusinessTone = {
  Icon: typeof BellRingIcon;
  label: string;
  iconWrapClassName: string;
  iconClassName: string;
};

const DEFAULT_TONE: BusinessTone = {
  Icon: BellRingIcon,
  label: 'Update',
  iconWrapClassName: 'border border-border bg-surface-alt/70',
  iconClassName: 'text-foreground/80',
};

const BUSINESS_INBOX_SKELETON_ROWS = Array.from({ length: 5 }, (_, index) => `skeleton-${index}`);

const BUSINESS_TONES: Record<string, BusinessTone> = {
  organization_invite_received: {
    Icon: UserRoundPlusIcon,
    label: 'Invite',
    iconWrapClassName: 'border border-primary/15 bg-primary/10',
    iconClassName: 'text-primary',
  },
  organization_invite_accepted: {
    Icon: BadgeCheckIcon,
    label: 'Accepted',
    iconWrapClassName: 'border border-status-live/15 bg-status-live/10',
    iconClassName: 'text-status-live',
  },
  organization_member_updated: {
    Icon: UserRoundPenIcon,
    label: 'Access',
    iconWrapClassName: 'border border-status-soon/15 bg-status-soon/10',
    iconClassName: 'text-status-soon',
  },
  organization_member_removed: {
    Icon: UserRoundMinusIcon,
    label: 'Removed',
    iconWrapClassName: 'border border-destructive/15 bg-destructive/10',
    iconClassName: 'text-destructive',
  },
  owner_handoff_started: {
    Icon: ShieldCheckIcon,
    label: 'Setup',
    iconWrapClassName: 'border border-primary/15 bg-primary/10',
    iconClassName: 'text-primary',
  },
  owner_setup_completed: {
    Icon: BadgeCheckIcon,
    label: 'Ready',
    iconWrapClassName: 'border border-status-live/15 bg-status-live/10',
    iconClassName: 'text-status-live',
  },
  venue_claim_approved: {
    Icon: BadgeCheckIcon,
    label: 'Approved',
    iconWrapClassName: 'border border-status-live/15 bg-status-live/10',
    iconClassName: 'text-status-live',
  },
  venue_rsvp: {
    Icon: BellRingIcon,
    label: 'RSVP',
    iconWrapClassName: 'border border-primary/15 bg-primary/10',
    iconClassName: 'text-primary',
  },
  event_comment: {
    Icon: BellRingIcon,
    label: 'Comment',
    iconWrapClassName: 'border border-status-soon/15 bg-status-soon/10',
    iconClassName: 'text-status-soon',
  },
  venue_followed: {
    Icon: UserRoundPlusIcon,
    label: 'Audience',
    iconWrapClassName: 'border border-primary/15 bg-primary/10',
    iconClassName: 'text-primary',
  },
};

export default function BusinessInboxScreen() {
  const { currentContext, currentBusinessMembership } = useAccountContext();
  const [selectedVenueID, setSelectedVenueID] = React.useState<string | null>(null);
  const organizationID = currentContext?.kind === 'business' ? currentContext.activeOrgId : null;
  const notificationsQuery = useNotifications({
    surface: 'business',
    organizationID,
    venueID: selectedVenueID,
  });
  const markRead = useMarkNotificationRead();
  const deleteNotification = useDeleteNotification();

  React.useEffect(() => {
    if (!currentBusinessMembership) {
      setSelectedVenueID(null);
      return;
    }

    if (
      selectedVenueID &&
      !currentBusinessMembership.venues.some((venue) => venue.venue_id === selectedVenueID)
    ) {
      setSelectedVenueID(null);
    }
  }, [currentBusinessMembership, selectedVenueID]);

  const notifications = notificationsQuery.data?.notifications ?? [];
  const eventPreviewURLs = useBusinessEventPreviewURLs(notifications);
  const renderBusinessNotificationSkeleton = React.useCallback<ListRenderItem<string>>(
    ({ index }) => <BusinessNotificationSkeleton showPreview={index % 2 === 0} />,
    []
  );
  const renderBusinessNotification = React.useCallback<ListRenderItem<NotificationItem>>(
    ({ item }) => {
      if (!organizationID || !currentBusinessMembership) {
        return null;
      }

      return (
        <BusinessNotificationCard
          item={item}
          organizationID={organizationID}
          venueName={
            item.payload.venue_id
              ? currentBusinessMembership.venues.find(
                  (venue) => venue.venue_id === item.payload.venue_id
                )?.venue_name ?? null
              : null
          }
          previewImage={resolveBusinessNotificationPreviewImage(item, eventPreviewURLs)}
          onDelete={(id) => {
            trackBusinessNotificationAction({
              action: 'dismiss',
              notificationID: id,
              notificationType: item.type,
              organizationID,
              venueID: item.payload.venue_id ?? null,
            });
            deleteNotification.mutate(id);
          }}
          onOpen={() => {
            trackBusinessNotificationAction({
              action: 'open',
              notificationID: item.id,
              notificationType: item.type,
              organizationID,
              venueID: item.payload.venue_id ?? null,
            });
            if (!item.is_read) {
              markRead.mutate(item.id);
            }
            openBusinessNotification(item, organizationID, currentBusinessMembership.role);
          }}
        />
      );
    },
    [currentBusinessMembership, deleteNotification, eventPreviewURLs, markRead, organizationID]
  );

  if (currentContext?.kind !== 'business' || !organizationID || !currentBusinessMembership) {
    return null;
  }

  const showVenueFilter = currentBusinessMembership.venues.length > 1;
  const selectedVenueName =
    selectedVenueID == null
      ? 'All venues'
      : currentBusinessMembership.venues.find((venue) => venue.venue_id === selectedVenueID)?.venue_name ??
        'Selected venue';
  const isRefreshing = notificationsQuery.isRefetching && !notificationsQuery.isPending;

  return (
    <AppScreen>
      <View className="px-4 pb-3 pt-4">
        <BusinessInboxHeader
          organizationName={currentBusinessMembership.organization_name}
          selectedVenueName={selectedVenueName}
          showVenueFilter={showVenueFilter}
          selectedVenueID={selectedVenueID}
          venues={currentBusinessMembership.venues}
          onSelectVenue={(venueID) => {
            trackBusinessNotificationAction({
              action: venueID ? 'filter_venue' : 'filter_all_venues',
              organizationID,
              venueID,
            });
            setSelectedVenueID(venueID);
          }}
        />
      </View>

      {notificationsQuery.isError ? (
        <View className="flex-1 px-4 pt-4">
          <ErrorState
            title="Couldn't load inbox."
            body={getAPIErrorRecoveryCopy(
              notificationsQuery.error,
              'workspace',
              'Unable to load business notifications.'
            )}
            actionLabel="Retry"
            onAction={() => notificationsQuery.refetch().catch(() => undefined)}
          />
        </View>
      ) : notificationsQuery.isPending ? (
        <FlatList
          data={BUSINESS_INBOX_SKELETON_ROWS}
          keyExtractor={(item) => item}
          renderItem={renderBusinessNotificationSkeleton}
          contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 40 }}
          showsVerticalScrollIndicator={false}
        />
      ) : (
        <FlatList
          data={notifications}
          keyExtractor={(item) => item.id}
          renderItem={renderBusinessNotification}
          contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 40 }}
          ListEmptyComponent={
            <View className="items-center px-6 pb-10 pt-12">
              <View className="size-14 items-center justify-center rounded-full bg-surface-alt">
                <Icon as={InboxIcon} size={24} className="text-muted-foreground" />
              </View>
              <Text variant="muted" className="mt-4 max-w-[280px] text-center leading-6 text-muted-foreground">
                {currentBusinessMembership.role === 'owner' || currentBusinessMembership.role === 'manager'
                  ? 'Team updates, approvals, and setup activity will show up here.'
                  : 'Venue updates and access changes will show up here.'}
              </Text>
            </View>
          }
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={() => notificationsQuery.refetch().catch(() => undefined)}
            />
          }
          showsVerticalScrollIndicator={false}
        />
      )}
    </AppScreen>
  );
}

function BusinessInboxHeader({
  organizationName,
  selectedVenueName,
  showVenueFilter,
  selectedVenueID,
  venues,
  onSelectVenue,
}: {
  organizationName: string;
  selectedVenueName: string;
  showVenueFilter: boolean;
  selectedVenueID: string | null;
  venues: { venue_id: string; venue_name: string }[];
  onSelectVenue: (venueID: string | null) => void;
}) {
  return (
    <View className="gap-5">
      <View className="gap-2 px-1">
        <Text className="text-4xl font-extrabold tracking-tight text-foreground">Inbox</Text>
        <Text variant="muted" className="leading-6">
          {organizationName}
        </Text>
      </View>

      {showVenueFilter ? (
        <View className="gap-3">
          <Text variant="small" className="px-1 uppercase tracking-[0.18em] text-muted-foreground">
            {selectedVenueName}
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
            <FilterChip label="All venues" active={selectedVenueID == null} onPress={() => onSelectVenue(null)} />
            {venues.map((venue) => (
              <FilterChip
                key={venue.venue_id}
                label={venue.venue_name}
                active={selectedVenueID === venue.venue_id}
                onPress={() => onSelectVenue(venue.venue_id)}
              />
            ))}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}

function FilterChip({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      className={cn(
        'rounded-full border px-4 py-2',
        active ? 'border-primary/20 bg-primary/10' : 'border-border bg-card'
      )}>
      <Text className={cn('text-sm font-semibold', active ? 'text-primary' : 'text-foreground')}>
        {label}
      </Text>
    </Pressable>
  );
}

function BusinessNotificationCard({
  item,
  organizationID,
  venueName,
  previewImage,
  onDelete,
  onOpen,
}: {
  item: NotificationItem;
  organizationID: string;
  venueName: string | null;
  previewImage: FocalEventPosterImageSource | null;
  onDelete: (id: string) => void;
  onOpen: () => void;
}) {
  const tone = BUSINESS_TONES[item.type] ?? DEFAULT_TONE;
  const timestamp = formatRelativeTime(item.created_at);
  const venueLabel =
    item.payload.venue_id && item.payload.organization_id === organizationID ? venueName : null;
  const showPreview = previewImage != null && item.type === 'venue_rsvp';
  const swipeableRef = React.useRef<Swipeable | null>(null);

  return (
    <Swipeable
      ref={swipeableRef}
      friction={2.5}
      overshootRight={false}
      rightThreshold={88}
      renderRightActions={() => (
        <DeleteAction
          onPress={() => {
            swipeableRef.current?.close();
            onDelete(item.id);
          }}
        />
      )}>
      <Pressable
        onPress={onOpen}
        className="border-b border-border/30 px-1 py-3.5 last:border-b-0">
        <View className="flex-row items-start gap-3">
          <NotificationIdentity item={item} tone={tone} venueLabel={venueLabel} />

          <View className="flex-1">
            <View className="flex-row items-start gap-3">
              <View className="flex-1 gap-1">
                <NotificationTitleRow item={item} timestamp={timestamp} />
                <Text variant="muted" className="leading-6 text-muted-foreground" numberOfLines={2}>
                  {businessNotificationBody(item, venueLabel)}
                </Text>
              </View>

              {showPreview ? (
                <FocalEventPosterImage
                  image={previewImage}
                  className="mt-0.5 h-[72px] w-[56px] rounded-[18px] border border-border/70 bg-surface-alt"
                />
              ) : null}
            </View>
          </View>
        </View>
      </Pressable>
    </Swipeable>
  );
}

function DeleteAction({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      className="ml-3 min-w-[92px] items-center justify-center self-stretch rounded-[22px] bg-destructive/14 active:bg-destructive/18">
      <View className="items-center gap-2">
        <Icon as={Trash2Icon} size={18} className="text-destructive" />
        <Text variant="small" className="font-semibold text-destructive">
          Dismiss
        </Text>
      </View>
    </Pressable>
  );
}

function BusinessNotificationSkeleton({ showPreview }: { showPreview: boolean }) {
  return (
    <View className="border-b border-border/30 px-1 py-3.5">
      <View className="flex-row items-start gap-3">
        <Skeleton className="size-12 rounded-full bg-surface-alt" />
        <View className="flex-1">
          <View className="flex-row items-start gap-3">
            <View className="flex-1 gap-2">
              <Skeleton className="h-4 w-40 rounded-full bg-surface-alt" />
              <Skeleton className="h-4 w-[88%] rounded-full bg-surface-alt" />
              <Skeleton className="h-4 w-[64%] rounded-full bg-surface-alt" />
            </View>
            {showPreview ? <Skeleton className="h-[72px] w-[56px] rounded-[18px] bg-surface-alt" /> : null}
          </View>
        </View>
      </View>
    </View>
  );
}

function useBusinessEventPreviewURLs(notifications: NotificationItem[]) {
  const request = useAuth((state) => state.request);
  const origin = useUserOriginCoords();
  const eventIDs = React.useMemo(
    () =>
      Array.from(
        new Set(
          notifications.flatMap((item) => {
            const eventID = item.payload.event_id;
            return typeof eventID === 'string' && eventID.length > 0 ? [eventID] : [];
          })
        )
      ),
    [notifications]
  );

  const queries = useQueries({
    queries: eventIDs.map((eventID) => ({
      queryKey: ['business-inbox-event-preview', eventID, origin.latitude, origin.longitude] as const,
      enabled: eventID.length > 0,
      staleTime: 60_000,
      queryFn: async () => {
        const params = new URLSearchParams({
          origin_lat: String(origin.latitude),
          origin_lng: String(origin.longitude),
        });
        if (origin.source === 'gps') {
          params.set('lat', String(origin.latitude));
          params.set('lng', String(origin.longitude));
        }
        const event = await request<RadiousEventDetail>(`/events/${eventID}?${params.toString()}`);
        return getCoverEventPosterImage(event);
      },
    })),
  });

  return React.useMemo(() => {
    const previewMap = new Map<string, FocalEventPosterImageSource>();
    queries.forEach((query, index) => {
      const eventID = eventIDs[index];
      if (!eventID || !query.data) {
        return;
      }
      previewMap.set(eventID, query.data);
    });
    return previewMap;
  }, [eventIDs, queries]);
}

function resolveBusinessNotificationPreviewImage(
  item: NotificationItem,
  eventPreviewURLs: Map<string, FocalEventPosterImageSource>
) {
  const payloadImageURL = item.payload.image_url?.trim() ?? '';
  if (payloadImageURL.length > 0) {
    return { public_url: payloadImageURL };
  }
  const eventID = item.payload.event_id?.trim() ?? '';
  if (eventID.length === 0) {
    return null;
  }
  return eventPreviewURLs.get(eventID) ?? null;
}

function NotificationIdentity({
  item,
  tone,
  venueLabel,
}: {
  item: NotificationItem;
  tone: BusinessTone;
  venueLabel: string | null;
}) {
  const username = item.payload.username?.trim() ?? '';
  const avatarURL = item.payload.avatar_url?.trim() ?? '';
  const displayName = item.payload.display_name?.trim() ?? '';

  if (
    username.length > 0 &&
    (item.type === 'venue_followed' || item.type === 'venue_rsvp' || item.type === 'event_comment')
  ) {
    return (
      <Avatar className="size-12">
        {avatarURL.length > 0 ? <AvatarImage source={{ uri: avatarURL }} /> : null}
        <AvatarFallback className="bg-primary/10">
          <Text className="text-sm font-semibold text-primary">
            {getProfileInitials((displayName || username).replace(/^@/, ''))}
          </Text>
        </AvatarFallback>
      </Avatar>
    );
  }

  if (venueLabel) {
    return (
      <View className="size-12 items-center justify-center rounded-2xl border border-primary/15 bg-primary/10">
        <Text className="text-xs font-bold uppercase tracking-[0.12em] text-primary">
          {getProfileInitials(venueLabel)}
        </Text>
      </View>
    );
  }

  return (
    <View className={cn('size-12 items-center justify-center rounded-2xl', tone.iconWrapClassName)}>
      <Icon as={tone.Icon} size={18} className={tone.iconClassName} />
    </View>
  );
}

function businessNotificationHeadline(item: NotificationItem) {
  switch (item.type) {
    case 'organization_invite_received':
      return 'Team invite'
    case 'organization_invite_accepted':
      return 'Invite accepted'
    case 'organization_member_updated':
      return 'Team access updated'
    case 'organization_member_removed':
      return 'Team access removed'
    case 'owner_handoff_started':
      return 'Owner setup in progress'
    case 'owner_setup_completed':
      return 'Access unlocked'
    case 'venue_claim_approved':
      return item.payload.title || 'Venue approved'
    case 'venue_rsvp':
      return 'New RSVP'
    case 'event_comment':
      return 'New comment'
    case 'venue_followed':
      return 'New follower'
    default:
      return item.payload.title || 'Update'
  }
}

function NotificationTitleRow({
  item,
  timestamp,
}: {
  item: NotificationItem;
  timestamp: string;
}) {
  const actorLabel = notificationActorLabel(item) ?? businessNotificationHeadline(item);

  return (
    <View className="flex-row items-center gap-1.5 pr-2">
      <Text className="flex-1 text-[15px] text-foreground" numberOfLines={1}>
        <Text className="font-semibold">{actorLabel}</Text>
        <Text className="font-normal text-muted-foreground">{` • ${timestamp}`}</Text>
      </Text>
    </View>
  );
}

function notificationActorLabel(item: NotificationItem) {
  if (item.type === 'venue_followed' || item.type === 'venue_rsvp' || item.type === 'event_comment') {
    return item.payload.display_name?.trim() || item.payload.username?.trim() || null;
  }
  return null;
}

function businessNotificationBody(item: NotificationItem, venueLabel: string | null) {
  switch (item.type) {
    case 'venue_followed':
      return `Started following ${venueLabel ?? 'this venue'}.`;
    case 'venue_rsvp':
      return `RSVPed to ${item.payload.title || 'an event'}${venueLabel ? ` at ${venueLabel}` : ''}.`;
    case 'event_comment':
      return `Commented on ${item.payload.title || 'an event'}${venueLabel ? ` at ${venueLabel}` : ''}.`;
    default:
      return item.payload.copy;
  }
}

function openBusinessNotification(
  item: NotificationItem,
  organizationID: string,
  role: 'owner' | 'manager' | 'social' | 'staff'
) {
  switch (item.type as NotificationType) {
    case 'organization_invite_received':
      if (openPayloadRoute(item.payload.route)) {
        return;
      }
      break;
    case 'organization_invite_accepted':
    case 'organization_member_updated':
    case 'organization_member_removed':
      router.push((role === 'owner' || role === 'manager' ? `/workspaces/${organizationID}/team` : `/workspaces/${organizationID}`) as never);
      return;
    case 'owner_handoff_started':
    case 'owner_setup_completed':
    case 'venue_claim_approved':
      if (openPayloadRoute(item.payload.route)) {
        return;
      }
      router.push(`/workspaces/${organizationID}` as never);
      return;
    case 'venue_rsvp':
    case 'event_comment':
      if (openPayloadRoute(item.payload.route)) {
        return;
      }
      router.push(`/workspaces/${organizationID}` as never);
      return;
    case 'venue_followed':
      if (item.payload.username) {
        router.push(`/profiles/${item.payload.username}` as never);
        return;
      }
      if (openPayloadRoute(item.payload.route)) {
        return;
      }
      router.push(`/workspaces/${organizationID}` as never);
      return;
    default:
      if (openPayloadRoute(item.payload.route)) {
        return;
      }
  }
}

function openPayloadRoute(route: string) {
  const normalized = route.trim();
  if (!isAllowedNotificationRoute(normalized)) {
    return false;
  }
  router.push(normalized as never);
  return true;
}
